import {
  useShoppingCartChangeLocale,
  useShoppingCartEmpty,
  useShoppingCartGetById,
  useShoppingCartGetMyIdAndVersion,
  useShoppingCartRemoveVoucher,
  useShoppingCartSetVoucher,
  WebshopProduct,
  WebshopShoppingCartEvent,
  WebshopShoppingCartQueries,
  WebshopShoppingCartVersioned,
} from '@cldsystems/ws-api-client';
import { useQueryClient } from '@tanstack/react-query';
import { useShallow } from 'zustand/react/shallow';
import useAccountLocale from '@/hooks/use-account-locale';
import { useAppContext } from '@/providers/app-provider/app-provider';
import { useKeycloak } from '@/providers/keycloak-provider';
import { MyCartPendingOperation } from '@/store/zustand/my-cart.slice';
import { useStore } from '@/store/zustand/zustand.setup';
import { MyCartActionButtonsEnum } from '@/types/action-buttons/action-buttons';
import { LocalMyCart } from '@/types/my-cart/local-my-cart';
import { SimpleProductInfo } from '@/types/products/simple-product-info';
import { WithQuantity } from '@/types/products/with-quantity';
import { findProductsBySkuAndLocale } from '@/utils/find-products-by-sku-and-locale';
import { getActualVersionFromError, isOptimisticLockError } from '@/utils/get-api-error-message';
import { mapProductToSimpleProductInfo } from '@/utils/map-product-to-simple-product-info';
import { priceCheck } from '@/utils/price-check';
import toConsentAwareTrackingEvent from '@/utils/to-consent-aware-tracking-event';
import useShoppingCartAddProductMutate from './use-shopping-cart-add-product-mutate';
import useShoppingCartRemoveProductMutate from './use-shopping-cart-remove-product-mutate';
import useShoppingCartSyncQuery from './use-shopping-cart-sync-query';
import useShoppingCartUpdateProductQuantityMutate from './use-shopping-cart-update-product-quntity-mutate';
import useShoppingCartValidateVoucher from './use-shopping-cart-validate-voucher';

type QueuedShoppingCartQuantityOperation = {
  shoppingCartId: string;
  product: WithQuantity<SimpleProductInfo>;
  quantity: number;
  event: WebshopShoppingCartEvent;
  buttonType: MyCartActionButtonsEnum;
  operationId: string;
  previousQuantity: number;
};

type QueuedShoppingCartAddOperation = {
  action: 'add';
  shoppingCartId: string;
  product: WithQuantity<SimpleProductInfo>;
  quantity: number;
  event: WebshopShoppingCartEvent;
  buttonType?: MyCartActionButtonsEnum;
  operationId: string;
  productWasInMyCart: boolean;
  previousQuantity: number;
};

type QueuedShoppingCartRemoveOperation = {
  action: 'remove';
  shoppingCartId: string;
  product: WithQuantity<SimpleProductInfo>;
  event: WebshopShoppingCartEvent;
  buttonType?: MyCartActionButtonsEnum;
  operationId: string;
  previousProduct?: WebshopShoppingCartVersioned['shoppingCart']['products'][number];
};

type QueuedShoppingCartProductOperation =
  | QueuedShoppingCartAddOperation
  | QueuedShoppingCartRemoveOperation;

let shoppingCartQuantityOperationCounter = 0;
const shoppingCartQuantityQueue: QueuedShoppingCartQuantityOperation[] = [];
let isProcessingShoppingCartQuantityQueue = false;
let shoppingCartProductOperationCounter = 0;
const shoppingCartProductQueue: QueuedShoppingCartProductOperation[] = [];
let isProcessingShoppingCartProductQueue = false;

/**
 * Global serialization point for EVERY cart write (quantity, add, remove,
 * voucher, empty). The API increments the cart `version` on every successful
 * mutation, so two writes that are in flight at once would both read the same
 * cached version and the second would be rejected as stale (409). The two queues
 * (quantity vs add/remove) plus the voucher/empty actions previously ran
 * concurrently — this mutex chains them so only one cart write is ever in flight,
 * letting each one read the freshest (optimistically-bumped) version.
 */
let cartWriteLock: Promise<unknown> = Promise.resolve();

const runExclusiveCartWrite = <T>(task: () => Promise<T>): Promise<T> => {
  const result = cartWriteLock.then(task, task);
  // Keep the chain alive even when a task rejects, but never let a prior
  // failure reject the lock itself (callers handle their own errors).
  cartWriteLock = result.then(
    () => undefined,
    () => undefined,
  );

  return result;
};

type UseMyCart = {
  myCart: LocalMyCart;
  decreaseProductQuantity: (
    product: WithQuantity<SimpleProductInfo>,
    event: WebshopShoppingCartEvent,
  ) => void;
  increaseProductQuantity: (
    product: WithQuantity<SimpleProductInfo>,
    event: WebshopShoppingCartEvent,
  ) => void;
  setProductQuantity: (
    product: WithQuantity<SimpleProductInfo>,
    quantity: number,
    event: WebshopShoppingCartEvent,
  ) => void;
  addProductToMyCart: (
    product: WithQuantity<SimpleProductInfo>,
    event: WebshopShoppingCartEvent,
    buttonType?: MyCartActionButtonsEnum,
  ) => void;
  removeProductFromMyCart: (
    product: WithQuantity<SimpleProductInfo>,
    event: WebshopShoppingCartEvent,
    buttonType?: MyCartActionButtonsEnum,
  ) => void;
  setMyCartVoucher: (voucher: string) => Promise<void>;
  removeMyCartVoucher: () => Promise<void>;
  isLoadingMyCart: boolean;
  isValidVoucher: boolean;
  emptyMyCart: () => Promise<void>;
  changeMyCartLocale: (newLocale: string) => Promise<void>;
  isPendingUpdateMyCartProductId: string | undefined;
  actionButtonType: MyCartActionButtonsEnum | undefined;
  getMyCartPending: (productId: string) => MyCartPendingOperation | undefined;
  isMyCartPending: (productId: string, buttonType?: MyCartActionButtonsEnum) => boolean;
};

const useMyCart = (): UseMyCart => {
  const { isAuthenticated, isInitialized } = useKeycloak();
  const { locale: accountLocale, isLoading: isAccountLocaleLoading } = useAccountLocale();
  const appContext = useAppContext<{ allProducts?: { items: WebshopProduct[] } } | undefined>();
  const allProducts = appContext?.allProducts?.items ?? [];
  // NOTE: post-deploy reconciliation lives in `<DeploymentVersionReconciler />`
  // (mounted from `<Page />`), NOT here. Keep this hook free of any Zustand
  // writes that could fire during render — it's called from many components
  // simultaneously (every `<ProductCard />`, `<LanguageSelector />`, etc.) and
  // any cross-component setState during their render trips React 19's guard.
  const queryClient = useQueryClient();
  const {
    myCart,
    setMyCart,
    addProductToMyCart,
    removeProductFromMyCart,
    increaseProductQuantity,
    decreaseProductQuantity,
    setProductQuantity,
    isValidVoucher,
    setIsValidVoucher,
    setMyCartVoucherValue,
    resetMyCart,
    markMyCartPending,
    clearMyCartPending,
    getMyCartPending,
    isMyCartPending,
    isPendingUpdateMyCartProductId,
    setIsPendingUpdateMyCartProductId,
    actionButtonType,
    setActionButtonType,
    openUpsellDialog,
  } = useStore(
    useShallow(state => ({
      myCart: state.myCart,
      setMyCart: state.setMyCart,
      addProductToMyCart: state.addProductToMyCart,
      removeProductFromMyCart: state.removeProductFromMyCart,
      increaseProductQuantity: state.increaseProductQuantity,
      decreaseProductQuantity: state.decreaseProductQuantity,
      setProductQuantity: state.setProductQuantity,
      isValidVoucher: state.isValidVoucher,
      setIsValidVoucher: state.setIsValidVoucher,
      setMyCartVoucherValue: state.setMyCartVoucherValue,
      resetMyCart: state.resetMyCart,
      markMyCartPending: state.markMyCartPending,
      clearMyCartPending: state.clearMyCartPending,
      getMyCartPending: state.getMyCartPending,
      isMyCartPending: state.isMyCartPending,
      isPendingUpdateMyCartProductId: state.isPendingUpdateMyCartProductId,
      setIsPendingUpdateMyCartProductId: state.setIsPendingUpdateMyCartProductId,
      actionButtonType: state.actionButtonType,
      setActionButtonType: state.setActionButtonType,
      openUpsellDialog: state.openUpsellDialog,
    })),
  );

  const getMyCartPendingSafe =
    typeof getMyCartPending === 'function' ? getMyCartPending : () => undefined;

  const isMyCartPendingSafe = typeof isMyCartPending === 'function' ? isMyCartPending : () => false;

  const shoppingCartGetMyIdAndVersion = useShoppingCartGetMyIdAndVersion({
    enabled: isAuthenticated,
  });
  const shoppingCartGetById = useShoppingCartGetById({
    params: { id: shoppingCartGetMyIdAndVersion.data?.id },
    enabled:
      isAuthenticated &&
      shoppingCartGetMyIdAndVersion.isSuccess &&
      !!shoppingCartGetMyIdAndVersion.data.id,
  });
  const shoppingCartValidateVoucher = useShoppingCartValidateVoucher();
  const shoppingCartAddProduct = useShoppingCartAddProductMutate(
    markMyCartPending,
    clearMyCartPending,
    getMyCartPendingSafe,
    setIsPendingUpdateMyCartProductId,
    setActionButtonType,
  );
  const shoppingCartRemoveProduct = useShoppingCartRemoveProductMutate(
    markMyCartPending,
    clearMyCartPending,
    getMyCartPendingSafe,
    setIsPendingUpdateMyCartProductId,
    setActionButtonType,
  );
  const shoppingCartUpdateProductQuantity = useShoppingCartUpdateProductQuantityMutate(
    markMyCartPending,
    clearMyCartPending,
    getMyCartPendingSafe,
    setIsPendingUpdateMyCartProductId,
    setActionButtonType,
  );
  const shoppingCartSetVoucher = useShoppingCartSetVoucher();
  const shoppingCartRemoveVoucher = useShoppingCartRemoveVoucher();

  const shoppingCartEmpty = useShoppingCartEmpty();
  const shoppingCartChangeLocale = useShoppingCartChangeLocale();

  const createShoppingCartQuantityOperationId = () => {
    shoppingCartQuantityOperationCounter += 1;

    return `shopping-cart-quantity-operation-${shoppingCartQuantityOperationCounter}`;
  };

  const createShoppingCartProductOperationId = () => {
    shoppingCartProductOperationCounter += 1;

    return `shopping-cart-product-operation-${shoppingCartProductOperationCounter}`;
  };

  const getShoppingCartQueryKey = (shoppingCartId: string) =>
    [WebshopShoppingCartQueries.GetById, shoppingCartId] as const;

  /**
   * Authoritative cart version to send with the next mutation. Always read from
   * the `GetById` cache (which every mutation's `onMutate` bumps optimistically)
   * so serialized writes send incrementing versions without waiting for a
   * server refetch. Falls back to the last fetched cart.
   */
  const getCurrentCartVersion = (shoppingCartId: string): number | undefined => {
    const cached = queryClient.getQueryData<WebshopShoppingCartVersioned>(
      getShoppingCartQueryKey(shoppingCartId),
    );

    return cached?.version ?? shoppingCartGetById.data?.version;
  };

  /**
   * Run a cart mutation and self-heal optimistic-lock conflicts: if the server's
   * version moved ahead of ours (code 3), reconcile to the real version and
   * replay the mutation exactly once. The first attempt suppresses the error
   * toast; the retry surfaces it, so the user is only notified when the conflict
   * can't be resolved automatically.
   *
   * Must be called inside `runExclusiveCartWrite` so the reconcile + replay stay
   * serialized with every other cart write.
   */
  const runWithVersionRetry = async (
    shoppingCartId: string,
    productId: string,
    operationId: string,
    mutate: (version: string, suppressVersionConflictToast: boolean) => Promise<unknown>,
  ): Promise<void> => {
    const currentVersion = getCurrentCartVersion(shoppingCartId);

    if (currentVersion == null) {
      clearMyCartPending(productId, operationId);
      return;
    }

    try {
      await mutate(currentVersion.toString(), true);
    } catch (error) {
      if (!isOptimisticLockError(error)) {
        throw error;
      }

      // Reconcile to the server's actual version. Prefer the version the error
      // reports (deterministic, no race); fall back to a refetch if absent.
      let actualVersion = getActualVersionFromError(error);

      if (actualVersion == null) {
        await queryClient.invalidateQueries({ queryKey: getShoppingCartQueryKey(shoppingCartId) });
        actualVersion = getCurrentCartVersion(shoppingCartId);
      }

      if (actualVersion == null) {
        throw error;
      }

      // Pin the cached version to the real one so the replay's optimistic bump
      // and the version we send line up with the server.
      const resolvedVersion = actualVersion;
      queryClient.setQueryData<WebshopShoppingCartVersioned>(
        getShoppingCartQueryKey(shoppingCartId),
        old => (old ? { ...old, version: resolvedVersion } : old),
      );

      await mutate(resolvedVersion.toString(), false);
    }
  };

  const applyOptimisticShoppingCartQuantityOperation = (
    operation: QueuedShoppingCartQuantityOperation,
  ) => {
    queryClient.setQueryData<WebshopShoppingCartVersioned>(
      getShoppingCartQueryKey(operation.shoppingCartId),
      old => {
        const currentShoppingCart = old ?? shoppingCartGetById.data;

        if (!currentShoppingCart) {
          return old;
        }

        const products = currentShoppingCart.shoppingCart.products.map(product =>
          product.id === operation.product.id
            ? { ...product, quantity: operation.quantity }
            : product,
        );
        const amount = products.reduce((acc, current) => acc + current.price * current.quantity, 0);
        const discountedAmount = products.reduce(
          (acc, current) => acc + current.discountedPrice * current.quantity,
          0,
        );

        return {
          ...currentShoppingCart,
          shoppingCart: {
            ...currentShoppingCart.shoppingCart,
            amount: priceCheck(amount),
            discountedAmount: priceCheck(discountedAmount),
            totalAmount: priceCheck(discountedAmount),
            products,
          },
        };
      },
    );
  };

  const applyOptimisticShoppingCartAddOperation = (operation: QueuedShoppingCartAddOperation) => {
    queryClient.setQueryData<WebshopShoppingCartVersioned>(
      getShoppingCartQueryKey(operation.shoppingCartId),
      old => {
        const currentShoppingCart = old ?? shoppingCartGetById.data;

        if (!currentShoppingCart) {
          return old;
        }

        const isProductExists = currentShoppingCart.shoppingCart.products.some(
          product => product.id === operation.product.id,
        );
        const products = isProductExists
          ? currentShoppingCart.shoppingCart.products.map(product =>
              product.id === operation.product.id
                ? { ...product, quantity: product.quantity + operation.quantity }
                : product,
            )
          : [
              ...currentShoppingCart.shoppingCart.products,
              {
                ...operation.product,
                quantity: operation.quantity,
                discountType: operation.product.discountType,
                display: operation.product.display,
                minOrderQuantity: 1,
              },
            ];
        const amount = products.reduce((acc, current) => acc + current.price * current.quantity, 0);
        const discountedAmount = products.reduce(
          (acc, current) => acc + current.discountedPrice * current.quantity,
          0,
        );

        return {
          ...currentShoppingCart,
          shoppingCart: {
            ...currentShoppingCart.shoppingCart,
            amount: priceCheck(amount),
            discountedAmount: priceCheck(discountedAmount),
            totalAmount: priceCheck(discountedAmount),
            products,
          },
        };
      },
    );
  };

  const applyOptimisticShoppingCartRemoveOperation = (
    operation: QueuedShoppingCartRemoveOperation,
  ) => {
    queryClient.setQueryData<WebshopShoppingCartVersioned>(
      getShoppingCartQueryKey(operation.shoppingCartId),
      old => {
        const currentShoppingCart = old ?? shoppingCartGetById.data;

        if (!currentShoppingCart) {
          return old;
        }

        const products = currentShoppingCart.shoppingCart.products.filter(
          product => product.id !== operation.product.id,
        );
        const amount = products.reduce((acc, current) => acc + current.price * current.quantity, 0);
        const discountedAmount = products.reduce(
          (acc, current) => acc + current.discountedPrice * current.quantity,
          0,
        );

        return {
          ...currentShoppingCart,
          shoppingCart: {
            ...currentShoppingCart.shoppingCart,
            amount: priceCheck(amount),
            discountedAmount: priceCheck(discountedAmount),
            totalAmount: priceCheck(discountedAmount),
            products,
          },
        };
      },
    );
  };

  const enqueueShoppingCartQuantityOperation = (operation: QueuedShoppingCartQuantityOperation) => {
    const queuedOperationIndex = shoppingCartQuantityQueue.findIndex(
      queuedOperation => queuedOperation.product.id === operation.product.id,
    );

    if (queuedOperationIndex === -1) {
      shoppingCartQuantityQueue.push(operation);
      return;
    }

    shoppingCartQuantityQueue.splice(queuedOperationIndex, 1, operation);
  };

  const enqueueShoppingCartProductOperation = (operation: QueuedShoppingCartProductOperation) => {
    shoppingCartProductQueue.push(operation);
  };

  /**
   * Reconcile the cart cache with the server once a burst has settled.
   *
   * Guarded two ways so a refetch can never clobber optimistic state mid-burst
   * (a late refetch returning a version that predates an in-flight write makes
   * the next write send a stale version -> 409 optimistic-lock failure / code 3):
   *  - skip while any cart write is still queued or in flight (either queue), so
   *    the other queue (or a follow-up burst) issues the reconcile instead, and
   *  - run the refetch through the shared write lock, so a new write waits for
   *    the reconciled version rather than racing the in-flight refetch.
   */
  const reconcileShoppingCartQueries = async (shoppingCartId: string) => {
    if (
      shoppingCartProductQueue.length > 0 ||
      shoppingCartQuantityQueue.length > 0 ||
      isProcessingShoppingCartProductQueue ||
      isProcessingShoppingCartQuantityQueue
    ) {
      return;
    }

    await runExclusiveCartWrite(() =>
      queryClient.invalidateQueries({ queryKey: getShoppingCartQueryKey(shoppingCartId) }),
    );
  };

  const processShoppingCartProductQueue = async () => {
    if (isProcessingShoppingCartProductQueue) {
      return;
    }

    isProcessingShoppingCartProductQueue = true;
    let processedShoppingCartId: string | undefined;

    try {
      while (shoppingCartProductQueue.length > 0) {
        const operation = shoppingCartProductQueue.shift();

        if (!operation) {
          continue;
        }

        processedShoppingCartId = operation.shoppingCartId;

        try {
          // The version is read (and reconciled on conflict) inside the lock so
          // it reflects any other cart write that just completed.
          await runExclusiveCartWrite(() =>
            runWithVersionRetry(
              operation.shoppingCartId,
              operation.product.id!,
              operation.operationId,
              (version, suppressVersionConflictToast) =>
                operation.action === 'add'
                  ? shoppingCartAddProduct.mutateAsync({
                      params: {
                        id: operation.shoppingCartId,
                        version,
                      },
                      payload: {
                        productId: operation.product.id!,
                        quantity: operation.quantity,
                        event: operation.event,
                        product: operation.product,
                        operationId: operation.operationId,
                        buttonType: operation.buttonType,
                        optimisticApplied: true,
                        productWasInMyCart: operation.productWasInMyCart,
                        previousQuantity: operation.previousQuantity,
                        suppressVersionConflictToast,
                      },
                    })
                  : shoppingCartRemoveProduct.mutateAsync({
                      params: {
                        id: operation.shoppingCartId,
                        version,
                      },
                      payload: {
                        productId: operation.product.id!,
                        event: operation.event,
                        product: operation.product,
                        operationId: operation.operationId,
                        buttonType: operation.buttonType,
                        optimisticApplied: true,
                        previousProduct: operation.previousProduct,
                        suppressVersionConflictToast,
                      },
                    }),
            ),
          );
        } catch {}
      }
    } finally {
      isProcessingShoppingCartProductQueue = false;

      if (processedShoppingCartId) {
        await reconcileShoppingCartQueries(processedShoppingCartId);
      }
    }
  };

  const processShoppingCartQuantityQueue = async () => {
    if (isProcessingShoppingCartQuantityQueue) {
      return;
    }

    isProcessingShoppingCartQuantityQueue = true;
    let processedShoppingCartId: string | undefined;

    try {
      while (shoppingCartQuantityQueue.length > 0) {
        const operation = shoppingCartQuantityQueue.shift();

        if (!operation) {
          continue;
        }

        processedShoppingCartId = operation.shoppingCartId;

        try {
          // The version is read (and reconciled on conflict) inside the lock so
          // it reflects any other cart write that just completed.
          await runExclusiveCartWrite(() =>
            runWithVersionRetry(
              operation.shoppingCartId,
              operation.product.id!,
              operation.operationId,
              (version, suppressVersionConflictToast) =>
                shoppingCartUpdateProductQuantity.mutateAsync({
                  params: {
                    id: operation.shoppingCartId,
                    version,
                  },
                  payload: {
                    productId: operation.product.id!,
                    quantity: operation.quantity,
                    event: operation.event,
                    product: operation.product,
                    operationId: operation.operationId,
                    buttonType: operation.buttonType,
                    optimisticApplied: true,
                    previousQuantity: operation.previousQuantity,
                    suppressVersionConflictToast,
                  },
                }),
            ),
          );
        } catch {}
      }
    } finally {
      isProcessingShoppingCartQuantityQueue = false;

      if (processedShoppingCartId) {
        await reconcileShoppingCartQueries(processedShoppingCartId);
      }
    }
  };

  const guestProductsForCartMerge = (() => {
    if (!isAuthenticated || !accountLocale || myCart.products.length === 0) {
      return myCart.products;
    }

    const needsReconciliation = myCart.products.some(
      product => product.locale?.toLowerCase() !== accountLocale,
    );

    if (!needsReconciliation) return myCart.products;

    const skus = myCart.products.map(product => product.sku);
    const matches = findProductsBySkuAndLocale(allProducts, skus, accountLocale);

    if (matches.length === 0) return myCart.products;

    const matchBySku = new Map(
      matches.map(match => [match.sku, mapProductToSimpleProductInfo(match)]),
    );

    return myCart.products
      .map(product => {
        const match = matchBySku.get(product.sku);
        return match ? { ...match, quantity: product.quantity } : undefined;
      })
      .filter((product): product is WithQuantity<SimpleProductInfo> => product != null);
  })();

  const { isSyncing: isCartSyncing } = useShoppingCartSyncQuery({
    payload: {
      products: guestProductsForCartMerge.map(p => ({ id: p.id!, quantity: p.quantity })),
      event: {
        name: '',
        id: '',
        sourceUrl: '',
      },
    },
    createEnabled:
      isAuthenticated &&
      !isAccountLocaleLoading &&
      shoppingCartGetMyIdAndVersion.isSuccess &&
      !shoppingCartGetMyIdAndVersion.data.id,
    addEnabled:
      isAuthenticated &&
      !isAccountLocaleLoading &&
      shoppingCartGetMyIdAndVersion.isSuccess &&
      !!shoppingCartGetMyIdAndVersion.data.id &&
      !!shoppingCartGetMyIdAndVersion.data.version &&
      guestProductsForCartMerge.length !== 0,
    shoppingCartId: shoppingCartGetMyIdAndVersion.data?.id,
    shoppingCartVersion: shoppingCartGetMyIdAndVersion.data?.version,
  });

  if (isAuthenticated) {
    return {
      myCart: {
        voucher: shoppingCartGetById.data?.shoppingCart.voucher?.voucher,
        products: shoppingCartGetById.data
          ? (shoppingCartGetById.data?.shoppingCart
              .products as unknown as WithQuantity<SimpleProductInfo>[])
          : [],
        amount: shoppingCartGetById.data?.shoppingCart.amount ?? 0,
        discountedAmount: shoppingCartGetById.data?.shoppingCart.discountedAmount ?? 0,
        totalAmount: shoppingCartGetById.data?.shoppingCart.totalAmount ?? 0,
      },
      isLoadingMyCart:
        isAccountLocaleLoading ||
        shoppingCartGetMyIdAndVersion.isLoading ||
        isCartSyncing ||
        shoppingCartGetById.isLoading,
      isValidVoucher: Boolean(shoppingCartGetById.data?.shoppingCart.voucher),
      increaseProductQuantity: (product, event) => {
        if (!shoppingCartGetById.isSuccess) return;
        const shoppingCartId = shoppingCartGetById.data.shoppingCart.id;
        const currentShoppingCart = queryClient.getQueryData<WebshopShoppingCartVersioned>(
          getShoppingCartQueryKey(shoppingCartId),
        );
        const currentProduct = currentShoppingCart?.shoppingCart.products.find(
          p => p.id === product.id,
        );
        const currentQuantity = currentProduct?.quantity ?? product.quantity;
        const nextQuantity = currentQuantity + 1;
        const consentAwareEvent = toConsentAwareTrackingEvent({
          name: event.name ?? '',
          id: event.id,
          sourceUrl: event.sourceUrl ?? '',
        });
        const operationId = createShoppingCartQuantityOperationId();
        const operation: QueuedShoppingCartQuantityOperation = {
          shoppingCartId,
          product,
          quantity: nextQuantity,
          event: consentAwareEvent,
          buttonType: MyCartActionButtonsEnum.INCREASE,
          operationId,
          previousQuantity: currentQuantity,
        };

        markMyCartPending(product.id!, MyCartActionButtonsEnum.INCREASE, operationId);
        applyOptimisticShoppingCartQuantityOperation(operation);
        enqueueShoppingCartQuantityOperation(operation);
        void processShoppingCartQuantityQueue();
      },
      decreaseProductQuantity: (product, event) => {
        if (!shoppingCartGetById.isSuccess) return;
        const shoppingCartId = shoppingCartGetById.data.shoppingCart.id;
        const currentShoppingCart = queryClient.getQueryData<WebshopShoppingCartVersioned>(
          getShoppingCartQueryKey(shoppingCartId),
        );
        const currentProduct = currentShoppingCart?.shoppingCart.products.find(
          p => p.id === product.id,
        );
        const currentQuantity = currentProduct?.quantity ?? product.quantity;
        const nextQuantity = currentQuantity - 1;
        const consentAwareEvent = toConsentAwareTrackingEvent({
          name: event.name ?? '',
          id: event.id,
          sourceUrl: event.sourceUrl ?? '',
        });
        const operationId = createShoppingCartQuantityOperationId();
        const operation: QueuedShoppingCartQuantityOperation = {
          shoppingCartId,
          product,
          quantity: nextQuantity,
          event: consentAwareEvent,
          buttonType: MyCartActionButtonsEnum.DECREASE,
          operationId,
          previousQuantity: currentQuantity,
        };

        markMyCartPending(product.id!, MyCartActionButtonsEnum.DECREASE, operationId);
        applyOptimisticShoppingCartQuantityOperation(operation);
        enqueueShoppingCartQuantityOperation(operation);
        void processShoppingCartQuantityQueue();
      },
      setProductQuantity: (product, quantity, event) => {
        if (!shoppingCartGetById.isSuccess) return;
        const shoppingCartId = shoppingCartGetById.data.shoppingCart.id;
        const currentShoppingCart = queryClient.getQueryData<WebshopShoppingCartVersioned>(
          getShoppingCartQueryKey(shoppingCartId),
        );
        const currentProduct = currentShoppingCart?.shoppingCart.products.find(
          p => p.id === product.id,
        );
        const consentAwareEvent = toConsentAwareTrackingEvent({
          name: event.name ?? '',
          id: event.id,
          sourceUrl: event.sourceUrl ?? '',
        });
        const operationId = createShoppingCartQuantityOperationId();
        const operation: QueuedShoppingCartQuantityOperation = {
          shoppingCartId,
          product,
          quantity,
          event: consentAwareEvent,
          buttonType: MyCartActionButtonsEnum.SET_QUANTITY,
          operationId,
          previousQuantity: currentProduct?.quantity ?? product.quantity,
        };

        markMyCartPending(product.id!, MyCartActionButtonsEnum.SET_QUANTITY, operationId);
        applyOptimisticShoppingCartQuantityOperation(operation);
        enqueueShoppingCartQuantityOperation(operation);
        void processShoppingCartQuantityQueue();
      },
      addProductToMyCart: (product, event, buttonType) => {
        if (!shoppingCartGetById.isSuccess) return;
        const shoppingCartId = shoppingCartGetById.data.shoppingCart.id;
        const currentShoppingCart = queryClient.getQueryData<WebshopShoppingCartVersioned>(
          getShoppingCartQueryKey(shoppingCartId),
        );
        const previousProduct = currentShoppingCart?.shoppingCart.products.find(
          shoppingCartProduct => shoppingCartProduct.id === product.id,
        );
        const consentAwareEvent = toConsentAwareTrackingEvent({
          name: event.name ?? '',
          id: event.id,
          sourceUrl: event.sourceUrl ?? '',
        });
        const operationId = createShoppingCartProductOperationId();
        const operation: QueuedShoppingCartAddOperation = {
          action: 'add',
          shoppingCartId,
          product,
          quantity: product.quantity,
          event: consentAwareEvent,
          buttonType,
          operationId,
          productWasInMyCart: Boolean(previousProduct),
          previousQuantity: previousProduct?.quantity ?? 0,
        };

        markMyCartPending(product.id!, buttonType, operationId);
        setIsPendingUpdateMyCartProductId(product.id);
        setActionButtonType(buttonType);
        applyOptimisticShoppingCartAddOperation(operation);
        // Adds triggered from the cart/favorites header tooltip shouldn't open
        // the upsell dialog — the user is already managing their lists there.
        // Adds from inside the upsell dialog's "you might also like" carousel
        // surface a toast instead (handled by the product card) so we don't
        // stack the dialog onto itself.
        if (
          buttonType !== MyCartActionButtonsEnum.TOOLTIP_ADD &&
          buttonType !== MyCartActionButtonsEnum.CROSS_UPSELL
        ) {
          openUpsellDialog(product);
        }
        enqueueShoppingCartProductOperation(operation);
        void processShoppingCartProductQueue();
      },
      removeProductFromMyCart: (product, event, buttonType) => {
        if (!shoppingCartGetById.isSuccess) return;
        const shoppingCartId = shoppingCartGetById.data.shoppingCart.id;
        const currentShoppingCart = queryClient.getQueryData<WebshopShoppingCartVersioned>(
          getShoppingCartQueryKey(shoppingCartId),
        );
        const previousProduct = currentShoppingCart?.shoppingCart.products.find(
          shoppingCartProduct => shoppingCartProduct.id === product.id,
        );

        // Guard against double-remove: the optimistic update below removes the
        // product from the cache synchronously, so a duplicate click (or a
        // second click before the row unmounts) finds no `previousProduct` and
        // is ignored. Without this the API would receive a remove for a product
        // it no longer has -> 409 "product not present in the shopping cart"
        // (code 1000).
        if (!previousProduct) {
          return;
        }

        const consentAwareEvent = toConsentAwareTrackingEvent({
          name: event.name ?? '',
          id: event.id,
          sourceUrl: event.sourceUrl ?? '',
        });
        const operationId = createShoppingCartProductOperationId();
        const operation: QueuedShoppingCartRemoveOperation = {
          action: 'remove',
          shoppingCartId,
          product,
          event: consentAwareEvent,
          buttonType,
          operationId,
          previousProduct,
        };

        markMyCartPending(product.id!, buttonType, operationId);
        setIsPendingUpdateMyCartProductId(product.id);
        setActionButtonType(buttonType);
        applyOptimisticShoppingCartRemoveOperation(operation);
        enqueueShoppingCartProductOperation(operation);
        void processShoppingCartProductQueue();
      },
      setMyCartVoucher: async voucher => {
        if (!shoppingCartGetById.isSuccess) return;
        const shoppingCartId = shoppingCartGetById.data.shoppingCart.id;

        await runExclusiveCartWrite(async () => {
          const currentVersion = getCurrentCartVersion(shoppingCartId);
          if (currentVersion == null) return;

          await shoppingCartSetVoucher.mutateAsync({
            params: {
              id: shoppingCartId,
              version: currentVersion.toString(),
            },
            payload: { voucher },
          });
        });
      },
      removeMyCartVoucher: async () => {
        if (!shoppingCartGetById.isSuccess) return;
        const shoppingCartId = shoppingCartGetById.data.shoppingCart.id;

        await runExclusiveCartWrite(async () => {
          const currentVersion = getCurrentCartVersion(shoppingCartId);
          if (currentVersion == null) return;

          await shoppingCartRemoveVoucher.mutateAsync({
            params: {
              id: shoppingCartId,
              version: currentVersion.toString(),
            },
          });
        });
      },
      emptyMyCart: async () => {
        const id = shoppingCartGetMyIdAndVersion.data?.id;

        if (!id) return;
        if (shoppingCartGetById.data?.shoppingCart.products.length === 0) return;

        await runExclusiveCartWrite(async () => {
          // Use the live (optimistically-bumped) cart version, not the stale
          // `GetMyIdAndVersion` snapshot, which earlier quantity/add/remove
          // mutations don't update.
          const currentVersion = getCurrentCartVersion(id);
          if (currentVersion == null) return;

          await shoppingCartEmpty.mutateAsync({
            params: {
              id,
              version: currentVersion.toString(),
            },
          });
        });
      },
      changeMyCartLocale: async (newLocale: string) => {
        const id = shoppingCartGetMyIdAndVersion.data?.id;

        if (!id) return;
        if (shoppingCartGetById.data?.shoppingCart.products.length === 0) return;

        await runExclusiveCartWrite(async () => {
          const currentVersion = getCurrentCartVersion(id);
          if (currentVersion == null) return;

          try {
            await shoppingCartChangeLocale.mutateAsync({
              params: {
                id,
                version: currentVersion.toString(),
              },
              payload: { locale: newLocale },
            });
            await queryClient.invalidateQueries({ queryKey: getShoppingCartQueryKey(id) });
          } catch {
            await shoppingCartEmpty.mutateAsync({
              params: {
                id,
                version: currentVersion.toString(),
              },
            });
          }
        });
      },

      isPendingUpdateMyCartProductId,
      actionButtonType,
      getMyCartPending: getMyCartPendingSafe,
      isMyCartPending: isMyCartPendingSafe,
    };
  }

  return {
    myCart,
    isLoadingMyCart: !isInitialized,
    isValidVoucher,
    addProductToMyCart: (product, _event, buttonType) => {
      addProductToMyCart(product);
      // Skip the upsell dialog for adds coming from the header tooltip and from
      // inside the upsell dialog itself (those show a toast instead).
      if (
        buttonType !== MyCartActionButtonsEnum.TOOLTIP_ADD &&
        buttonType !== MyCartActionButtonsEnum.CROSS_UPSELL
      ) {
        openUpsellDialog(product);
      }
    },
    increaseProductQuantity,
    decreaseProductQuantity,
    setProductQuantity,
    removeProductFromMyCart,
    setMyCartVoucher: async voucher => {
      try {
        await shoppingCartValidateVoucher.mutateAsync({
          payload: {
            id: '',
            voucher,
            products: myCart.products.map(product => ({
              id: product.id!,
              quantity: product.quantity,
            })),
          },
        });
        setMyCartVoucherValue(voucher);
        setIsValidVoucher(true);
      } catch (err) {
        setIsValidVoucher(false);
        throw err;
      }
    },
    removeMyCartVoucher: async () => {
      setMyCartVoucherValue('');
      setIsValidVoucher(false);
    },
    emptyMyCart: async () => {
      resetMyCart();
    },
    changeMyCartLocale: async (newLocale: string) => {
      if (myCart.products.length === 0) return;

      const skus = myCart.products.map(product => product.sku);
      const matches = findProductsBySkuAndLocale(allProducts, skus, newLocale);

      if (matches.length === 0) {
        resetMyCart();
        return;
      }

      const matchBySku = new Map(
        matches.map(match => [match.sku, mapProductToSimpleProductInfo(match)]),
      );

      const products = myCart.products
        .map(product => {
          const match = matchBySku.get(product.sku);
          return match ? { ...match, quantity: product.quantity } : undefined;
        })
        .filter((product): product is WithQuantity<SimpleProductInfo> => product != null);

      const amount = products.reduce((acc, p) => acc + p.price * p.quantity, 0);
      const discountedAmount = products.reduce((acc, p) => acc + p.discountedPrice * p.quantity, 0);

      setMyCart({
        products,
        amount: priceCheck(amount),
        discountedAmount: priceCheck(discountedAmount),
        totalAmount: priceCheck(discountedAmount),
      });
    },
    isPendingUpdateMyCartProductId,
    actionButtonType,
    getMyCartPending: getMyCartPendingSafe,
    isMyCartPending: isMyCartPendingSafe,
  };
};

export default useMyCart;
